import {StrictMode, useState, useEffect, useCallback, useContext} from 'react'
import {createRoot} from 'react-dom/client'
import {BrowserRouter} from 'react-router-dom'
import {ConfigProvider, App as AntdApp} from 'antd' // ⬅️ добавили App как AntdApp
import {QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {lightTheme, darkTheme} from './theme.js'
import {ThemeModeContext} from './ThemeModeContext.js'
import {LanguageContext, LanguageProvider} from './LanguageContext.jsx'
import {installUnauthorizedHandler} from './utils/unauthorizedHandler.js'
import {getNormalizedProfileSettings, normalizeUserSettings} from './utils/auth.js'
import {bootstrapApplicationRuntime} from '@runtime/bootstrap.js'
import AppErrorBoundary from '@runtime/AppErrorBoundary.jsx'
import '@shared/theme/generated-tokens.css'
import './i18n.js'
import './index.css'
import App from './App.jsx'

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            retry: 1,
        },
    },
})

installUnauthorizedHandler()

function LanguageAwareProviders({theme}) {
    const {antLocale} = useContext(LanguageContext)

    return (
        <ConfigProvider theme={theme} locale={antLocale}>
            <AntdApp>
                <QueryClientProvider client={queryClient}>
                    <BrowserRouter>
                        <App/>
                    </BrowserRouter>
                </QueryClientProvider>
            </AntdApp>
        </ConfigProvider>
    )
}

function Root() {
    const [themeMode, setThemeModeState] = useState(() => {
        const {themeMode: profileTheme} = getNormalizedProfileSettings()
        if (profileTheme) return profileTheme
        const stored = normalizeUserSettings({themeMode: localStorage.getItem('themeMode')}).themeMode
        return stored || 'dark'
    })

    useEffect(() => {
        localStorage.setItem('themeMode', themeMode)

        const rootElement = document.documentElement
        rootElement.setAttribute('data-theme', themeMode)

        const rootStyles = getComputedStyle(rootElement)
        const backgroundColor =
            rootStyles.getPropertyValue('--ant-color-bg-base')?.trim() ||
            rootStyles.getPropertyValue('--color-surface-base')?.trim()

        const themeColorMeta = document.querySelector("meta[name='theme-color']")
        if (themeColorMeta && backgroundColor) {
            themeColorMeta.setAttribute('content', backgroundColor)
        }

        const statusBarMeta = document.querySelector("meta[name='apple-mobile-web-app-status-bar-style']")
        if (statusBarMeta) {
            statusBarMeta.setAttribute('content', themeMode === 'dark' ? 'black-translucent' : 'default')
        }

        return () => {
            rootElement.removeAttribute('data-theme')
        }
    }, [themeMode])

    const setThemeMode = useCallback((mode) => {
        const normalized = normalizeUserSettings({themeMode: mode}).themeMode || 'dark'
        setThemeModeState(normalized)
    }, [])

    const toggleTheme = () => {
        const next = themeMode === 'dark' ? 'light' : 'dark'
        setThemeMode(next)
    }


    const theme = themeMode === 'dark' ? darkTheme : lightTheme

    return (
        <ThemeModeContext.Provider value={{themeMode, toggleTheme, setThemeMode}}>
            <LanguageProvider>
                <AppErrorBoundary>
                    <LanguageAwareProviders theme={theme}/>
                </AppErrorBoundary>
            </LanguageProvider>
        </ThemeModeContext.Provider>
    )
}

async function startApp() {
    await bootstrapApplicationRuntime()

    createRoot(document.getElementById('root')).render(
        <StrictMode>
            <Root/>
        </StrictMode>,
    )
}

startApp()
